import { useState } from "react";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { getApprovalFileId } from "../utils/getApprovalFileId";
import {
  bulkMovePersonalItems,
  createPersonalSpaceFolder,
  createTrainedDocumentFolder,
  deletePersonalFile,
  deleteTrainedDocuments,
  deleteUnorganizedDoc,
  downloadPersonalFile,
  downloadTrainedDocument,
  markPersonalFileInactive,
  moveOrganizedItems,
  moveUnorganizedFiles,
  revertPersonalFile,
  renamePersonalSpaceItem,
  renameRepoItem,
  triggerBlobDownload,
  updateApprovalStatus,
  updateFileStatus,
  updateQaTextStatus,
  uploadForTraining,
  uploadPersonalFiles,
  downloadFileForTraining,
} from "../services/repoOrganizerService";
import {
  getRepoItemBackendId,
  getRepoMovePayloadItem,
} from "../utils/repoItemIdentity";

export function useRepoItemActions({
  items,
  setItems,
  fetchItems,
  role,
  repoView,
  currentFolder,
  createFolderModal,
  setCreateFolderModal,
  deleteModal,
  setDeleteModal,
  setSelectedItems,
}) {
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("Global");
  const [showUploadModeDropdown, setShowUploadModeDropdown] = useState(false);
  const [dataName, setDataName] = useState("");
  const [description, setDescription] = useState("");
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFileToTrain, setSelectedFileToTrain] = useState(null);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const handleNavigate = (
    folderId,
    folderName,
    setCurrentFolder,
    setBreadcrumbs,
  ) => {
    const safeFolderId = folderId ? String(folderId) : null;
    setCurrentFolder(safeFolderId);
    setSelectedItems(new Set());
    if (safeFolderId === null) {
      setBreadcrumbs([{ id: null, name: "Root" }]);
    } else {
      setBreadcrumbs((prev) => {
        const index = prev.findIndex((b) => b.id === safeFolderId);
        if (index !== -1) return prev.slice(0, index + 1);
        return [...prev, { id: safeFolderId, name: folderName }];
      });
    }
  };

  const handleCreateFolder = async () => {
    const folderName = createFolderModal.name.trim();
    if (!folderName) {
      showErrorToast("Folder name cannot be empty");
      return;
    }

    const isPS = repoView === "personal_space";
    const duplicateExists = items.some((item) => {
      if (item.type !== "folder") return false;
      if (item.parentId !== currentFolder) return false;
      if (item.name?.trim().toLowerCase() !== folderName.toLowerCase())
        return false;
      if (isPS) return item.isPersonalSpace === true;
      if (repoView === "global")
        return (
          item.ownerType === "admin" ||
          item.mode === "global" ||
          item.mode === "group"
        );
      return (
        item.ownerType !== "admin" &&
        item.mode !== "global" &&
        item.mode !== "group"
      );
    });

    if (duplicateExists) {
      showErrorToast("Folder with this name already exists");
      return;
    }

    try {
      let createdFolder;
      if (isPS) {
        const res = await createPersonalSpaceFolder(folderName, currentFolder);
        const createdFolderId = res.data?.folder?.user_files_id;
        if (!createdFolderId) throw new Error("Failed to create folder");

        createdFolder = {
          id: `ps-folder-${createdFolderId}`,
          name: folderName,
          type: "folder",
          parentId: currentFolder,
          size: 0,
          updatedAt: new Date().toISOString(),
          uploadedBy: "Me",
          isPersonalSpace: true,
          ownerType: "user",
          mode: "personal",
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: "1",
          createdAt: new Date().toISOString(),
        };
      } else {
        const res = await createTrainedDocumentFolder(
          folderName,
          currentFolder,
        );
        const createdFolderId = res.data?.data?.id;
        if (!createdFolderId) throw new Error("Failed to create folder");

        createdFolder = {
          id: String(createdFolderId),
          name: folderName,
          type: "folder",
          parentId: currentFolder,
          size: 0,
          updatedAt: new Date().toISOString(),
          uploadedBy: "Me",
          isPersonalSpace: false,
          ownerType: role === "admin" ? "admin" : "user",
          mode: role === "admin" ? "global" : "personal",
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: "1",
          createdAt: new Date().toISOString(),
        };
      }

      setItems((prev) => [...prev, createdFolder]);
      setCreateFolderModal({ show: false, name: "" });
      showSuccessToast("Folder created successfully");
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || "Failed to create folder";
      showErrorToast(errorMessage);
    }
  };

  const handleDelete = async (strategy = "DELETE") => {
    const itemsToDelete = deleteModal.items;
    const organizedIds = itemsToDelete
      .filter((i) => !i.id.toString().startsWith("unorganized-"))
      .map((i) => getRepoItemBackendId(i));
    const unorganizedIds = itemsToDelete
      .filter((i) => i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      if (
        deleteModal.items.length === 1 &&
        deleteModal.items[0].isPersonalSpace
      ) {
        const item = deleteModal.items[0];
        if (item.fileStatus === "1") {
          await markPersonalFileInactive(item.originalId);
          showSuccessToast("Moved to Deleted");
        } else {
          await deletePersonalFile(item.originalId);
          showSuccessToast("Permanently deleted");
        }
        setDeleteModal({ show: false, items: [] });
        fetchItems();
        return;
      }

      const deletedIds = new Set(itemsToDelete.map((i) => i.id));
      setItems((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setDeleteModal({ show: false, items: [] });
      setSelectedItems(new Set());

      showSuccessToast(
        itemsToDelete.length > 1
          ? `Deleted ${itemsToDelete.length} items`
          : "Item deleted",
      );

      await Promise.all([
        organizedIds.length > 0
          ? deleteTrainedDocuments(organizedIds, strategy)
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) => deleteUnorganizedDoc(fileId)),
      ]);
    } catch (err) {
      console.error("Delete error:", err);
      showErrorToast("Delete failed");
      fetchItems();
    }
  };

  const handleDownload = async (item) => {
    if (item.type === "folder") return;
    try {
      if (item.isPersonalSpace) {
        const res = await downloadPersonalFile(item.originalId);
        triggerBlobDownload(res.data, item.name);
        return;
      }
      const idToUse = item.uploaded_file_id || item.id;
      const res = await downloadTrainedDocument(idToUse);
      triggerBlobDownload(res.data, item.name);
    } catch (err) {
      console.error("Download failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Download failed");
          } catch (e) {
            showErrorToast("Download failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Download failed");
      }
    }
  };

  const handleBulkDelete = async (selectedIds) => {
    const itemsToDelete = items.filter((i) => selectedIds.has(i.id));
    const organizedIds = itemsToDelete
      .filter((i) => !i.isUnorganized)
      .map((i) => getRepoItemBackendId(i));
    const unorganizedIds = itemsToDelete
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      setItems((prev) => prev.filter((item) => !selectedIds.has(item.id)));
      setSelectedItems(new Set());
      showSuccessToast(`Deleted ${itemsToDelete.length} items`);

      await Promise.all([
        organizedIds.length > 0
          ? deleteTrainedDocuments(organizedIds, "DELETE")
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) => deleteUnorganizedDoc(fileId)),
      ]);
    } catch (err) {
      console.error("Bulk delete error:", err);
      showErrorToast("Bulk delete failed");
      fetchItems();
    }
  };

  const handleMove = async (itemsToMove, targetFolderId) => {
    if (repoView === "personal_space") {
      try {
        const targetParentId = targetFolderId
          ? targetFolderId.replace("ps-folder-", "")
          : null;
        const itemIds = itemsToMove.map((item) => item.originalId);

        const movedItemIds = new Set(itemsToMove.map((i) => i.id));
        setItems((prev) =>
          prev.map((item) =>
            movedItemIds.has(item.id)
              ? { ...item, parentId: targetFolderId }
              : item,
          ),
        );

        setSelectedItems(new Set());
        showSuccessToast(`Moved ${itemsToMove.length} items`);

        await bulkMovePersonalItems(itemIds, targetParentId);
      } catch (err) {
        console.error("Move error:", err);
        showErrorToast(err?.response?.data?.message || "Move failed");
        fetchItems();
      }
      return;
    }

    const unorganizedIds = itemsToMove
      .filter((i) => i.isUnorganized)
      .map((i) => getRepoItemBackendId(i));
    const unsupportedMoveItems = itemsToMove.filter(
      (item) => !item.isUnorganized && !getRepoMovePayloadItem(item),
    );
    if (unsupportedMoveItems.length > 0) {
      showErrorToast("Some selected items cannot be moved");
      return;
    }
    const organizedItems = itemsToMove 
      .filter((i) => !i.isUnorganized)
      .map(getRepoMovePayloadItem);

    try {
      const movedItemIds = new Set(itemsToMove.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          movedItemIds.has(item.id)
            ? { ...item, parentId: targetFolderId }
            : item,
        ),
      );

      setSelectedItems(new Set());
      showSuccessToast(`Moved ${itemsToMove.length} items`);

      await Promise.all([
        unorganizedIds.length > 0
          ? moveUnorganizedFiles(unorganizedIds, targetFolderId)
          : Promise.resolve(),
        organizedItems.length > 0
          ? moveOrganizedItems(organizedItems, targetFolderId)
          : Promise.resolve(),
      ]);
    } catch (err) {
      console.error("Move error:", err);
      showErrorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Move failed",
      );
      fetchItems();
    }
  };

  const toggleSelect = (id, setSelectedItemsState) => {
    setSelectedItemsState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChangeFileStatus = async (item) => {
    if (item.isPersonalSpace) {
      try {
        if (item.fileStatus === "0") {
          if (updatingItems.has(item.id)) return;
          setUpdatingItems((prev) => new Set(prev).add(item.id));
          await revertPersonalFile(item.originalId);
          showSuccessToast("Restored to Saved");
          fetchItems();
        }
      } catch (err) {
        console.error("Error restoring file:", err);
        showErrorToast(err?.response?.data?.message || "Something went wrong");
      } finally {
        if (item.fileStatus === "0") {
          setUpdatingItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      }
      return;
    }

    console.log("Change status item:", item);

    const fileId =
      item.pythonFileUuid || item.uploaded_file_id || item.originalId;

    if (!fileId) {
      showErrorToast("File identifier missing");
      return;
    }

    if (updatingItems.has(item.id)) return;

    try {
      setUpdatingItems((prev) => new Set(prev).add(item.id));
      const newStatus = item.fileStatus === "1" ? "0" : "1";

      await updateFileStatus(fileId, item.fileType, newStatus);
      
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, fileStatus: newStatus } : i,
        ),
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleChangeApprovalStatus = async (itemId, newStatus) => {
    if (updatingItems.has(itemId)) return;

    const approvalFileId = getApprovalFileId(
      items.find((item) => item.id === itemId) || { id: itemId },
    );

    try {
      setUpdatingItems((prev) => new Set(prev).add(itemId));
      await updateApprovalStatus(approvalFileId, newStatus);

      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, approvalStatus: newStatus } : item,
        ),
      );

      showSuccessToast(`Doc status set to ${newStatus}`);
    } catch (err) {
      console.error("Error updating approval status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleBulkApprovalStatus = async (newStatus, selectedItems) => {
    if (isBulkUpdating) return;
    const fileItems = items.filter(
      (i) => selectedItems.has(i.id) && i.type === "file",
    );
    if (fileItems.length === 0) {
      showErrorToast("No files selected");
      return;
    }
    try {
      setIsBulkUpdating(true);
      await Promise.all(
        fileItems.map((f) =>
          updateApprovalStatus(getApprovalFileId(f), newStatus),
        ),
      );

      const updateIds = new Set(fileItems.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          updateIds.has(item.id)
            ? { ...item, approvalStatus: newStatus }
            : item,
        ),
      );
      setSelectedItems(new Set());

      showSuccessToast(`${fileItems.length} file(s) set to ${newStatus}`);
    } catch (err) {
      console.error("Bulk approval error:", err);
      showErrorToast("Failed to update one or more files");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkDownload = async (selectedIds) => {
    const selectedList = items.filter((i) => selectedIds.has(i.id));
    const foldersSelected = selectedList.filter((i) => i.type === "folder");
    const filesToDownload = selectedList.filter((i) => i.type === "file");

    if (foldersSelected.length > 0 && filesToDownload.length > 0) {
      showErrorToast(
        "Folders cannot be downloaded. Only files will be processed.",
      );
    } else if (foldersSelected.length > 0) {
      showErrorToast("Cannot download folders.");
      return;
    }

    if (filesToDownload.length === 0) {
      showErrorToast("No files selected for download");
      return;
    }

    showSuccessToast(`Starting download of ${filesToDownload.length} files...`);

    for (const file of filesToDownload) {
      await handleDownload(file);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const handleProcessFile = (file) => {
    setSelectedFileToTrain(file);
    setShowFirstModal(true);
  };

  const openRenameModal = (item) => {
    setRenameTarget(item);
    setRenameText(item.name || "");
    setShowRenameModal(true);
  };

  const closeRenameModal = () => {
    setShowRenameModal(false);
    setRenameTarget(null);
    setRenameText("");
    setIsRenaming(false);
  };

  const getRenamedItemDisplayName = (item, responseData, fallbackName) => {
    if (item?.isPersonalSpace) {
      return (
        responseData?.item?.user_files_name ||
        responseData?.item?.name ||
        fallbackName
      );
    }

    const updatedItem = responseData?.updatedItem;
    if (!updatedItem) {
      return fallbackName;
    }

    if (item?.itemSource === "user_upload") {
      return updatedItem.text_title || updatedItem.question || fallbackName;
    }

    if (item?.itemSource === "uploaded_file") {
      return (
        updatedItem.original_filename ||
        updatedItem.filename ||
        fallbackName
      );
    }

    return updatedItem.name || fallbackName;
  };

  const performRename = async () => {
    if (!renameTarget) {
      showErrorToast("No item selected for rename");
      return;
    }

    const newName = renameText.trim();
    if (!newName) {
      showErrorToast("Name cannot be empty");
      return;
    }

    const duplicateExists = items.some((item) => {
      if (item.parentId !== renameTarget.parentId) return false;
      if (item.id === renameTarget.id) return false;
      return item.name?.trim().toLowerCase() === newName.toLowerCase();
    });

    if (duplicateExists) {
      showErrorToast("An item with this name already exists");
      return;
    }

    try {
      setIsRenaming(true);
      let response;
      if (renameTarget.isPersonalSpace) {
        response = await renamePersonalSpaceItem(
          getRepoItemBackendId(renameTarget),
          newName,
        );
      } else {
        response = await renameRepoItem(renameTarget, newName);
      }

      const updatedName = getRenamedItemDisplayName(
        renameTarget,
        response?.data,
        newName,
      );

      setItems((prev) =>
        prev.map((item) =>
          item.id === renameTarget.id ? { ...item, name: updatedName } : item,
        ),
      );

      showSuccessToast("Item renamed successfully");
      closeRenameModal();
    } catch (err) {
      console.error("❌ Rename failed", err);
      showErrorToast(err?.response?.data?.message || err?.response?.data?.error || "Rename failed");
      setIsRenaming(false);
    }
  };

  const handleAction = (action, item, setMoveModalState) => {
    switch (action) {
      case "download":
        handleDownload(item);
        break;
      case "status":
        handleChangeFileStatus(item);
        break;
      case "process":
        handleProcessFile(item);
        break;
      case "revert":
        handleChangeFileStatus(item);
        break;
      case "delete":
        setDeleteModal({ show: true, items: [item] });
        break;
      case "move":
        setMoveModalState({ show: true, items: [item], targetFolderId: null });
        break;
      case "rename":
        openRenameModal(item);
        break;
      default:
        break;
    }
  };

  const handleSendForTraining = async (file, mode) => {
    try {
      setIsTraining(true);
      setShowTrainingModal(false);
      showSuccessToast(`Sending ${file.name} for training...`);

      const downloadRes = await downloadFileForTraining(file.originalId);
      const blob = downloadRes.data;
      const selectedFile = new File([blob], file.name, {
        type: file.fileType || blob.type || "application/octet-stream",
      });

      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("mode", mode.toLowerCase());
      formData.append("model", "llama-3.3-70b-versatile");

      const response = await uploadForTraining(formData);

      if (response.status === 200 || response.status === 207) {
        showSuccessToast(`${file.name} trained successfully!`);
        await markPersonalFileInactive(file.originalId);
        fetchItems();
      }
    } catch (err) {
      console.error("Training failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Training failed");
          } catch (e) {
            showErrorToast("Training failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Training failed");
      }
    } finally {
      setIsTraining(false);
      setSelectedFileToTrain(null);
    }
  };

  const uploadToBackend = async () => {
    try {
      setIsSubmitting(true);
      const firstItem = summaryDataList[0];
      if (firstItem && firstItem.originalId) {
        await markPersonalFileInactive(firstItem.originalId);
      }

      setShowSummaryModal(false);
      setSummaryDataList([]);
      fetchItems();
      showSuccessToast("Training completed!");
    } catch (err) {
      console.error("Submission failed:", err);
      showErrorToast("Submission failed!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e, setIsLoading) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      await uploadPersonalFiles(formData);
      showSuccessToast("Files uploaded successfully");
      fetchItems();
    } catch (error) {
      console.error("Upload failed:", error);
      showErrorToast("Upload failed");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  return {
    showFirstModal,
    setShowFirstModal,
    showSummaryModal,
    setShowSummaryModal,
    uploadedFiles,
    setUploadedFiles,
    uploadMode,
    setUploadMode,
    showUploadModeDropdown,
    setShowUploadModeDropdown,
    dataName,
    setDataName,
    description,
    setDescription,
    summaryDataList,
    setSummaryDataList,
    isSubmitting,
    selectedFileToTrain,
    showTrainingModal,
    setShowTrainingModal,
    isTraining,
    updatingItems,
    isBulkUpdating,
    handleNavigate,
    handleCreateFolder,
    handleDelete,
    handleDownload,
    handleBulkDelete,
    handleMove,
    toggleSelect,
    handleChangeFileStatus,
    handleChangeApprovalStatus,
    handleBulkApprovalStatus,
    handleBulkDownload,
    handleProcessFile,
    handleAction,
    handleSendForTraining,
    uploadToBackend,
    handleFileChange,
    showRenameModal,
    renameTarget,
    renameText,
    setRenameText,
    isRenaming,
    closeRenameModal,
    performRename,
  };
}
